import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { ArrowLeft, Printer, Save, Plus, Wrench } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { api, dataIt, STATI_PREVENTIVO, formatError, totaliScheda } from "@/lib/api";
import { TotaliConSconto } from "@/components/ScontoInput";
import { PageHeader } from "@/components/PageHeader";
import { SearchSelect } from "@/components/SearchSelect";
import { RicambiRows, ManodoperaRows } from "@/components/SchedaRows";
import { ConfirmDelete } from "@/components/ConfirmDelete";
import { EntityFormDialog } from "@/components/EntityFormDialog";
import { CLIENTE_FIELDS } from "@/pages/Clienti";
import { biciFields } from "@/pages/Bici";
import { scadenza } from "@/pages/Preventivi";

const today = () => new Date().toISOString().slice(0, 10);
const EMPTY = { cliente_id: "", bici_id: "", data: today(), validita_giorni: 30, problema: "", lavori_previsti: "", stato: "bozza", ricambi: [], manodopera: [], note: "", sconto_totale: 0, sconto_totale_tipo: "percentuale" };
const L = ({ children }) => <Label className="mb-1.5 block text-xs font-bold uppercase tracking-wider text-slate-500">{children}</Label>;
const Section = ({ title, children, testId }) => (
  <section className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm" data-testid={testId}>
    <h2 className="mb-4 font-heading text-base font-bold text-slate-900">{title}</h2>{children}
  </section>
);

export default function PreventivoForm() {
  const { id } = useParams();
  const [sp] = useSearchParams();
  const navigate = useNavigate();
  const [f, setF] = useState({ ...EMPTY, cliente_id: sp.get("cliente") || "", bici_id: sp.get("bici") || "" });
  const [meta, setMeta] = useState({});
  const [clienti, setClienti] = useState([]);
  const [bici, setBici] = useState([]);
  const [magazzino, setMagazzino] = useState([]);
  const [tariffa, setTariffa] = useState(35);
  const [saving, setSaving] = useState(false);
  const [dlg, setDlg] = useState(null);

  const loadRefs = useCallback(async () => {
    const [c, b, r, imp, pr] = await Promise.all([api.get("/clienti"), api.get("/bici"), api.get("/ricambi"), api.get("/impostazioni"), api.get("/prodotti")]);
    setClienti(c.data); setBici(b.data); setMagazzino([...r.data.map((x) => ({ ...x, tipo: "ricambio" })), ...pr.data.map((x) => ({ ...x, tipo: "prodotto" }))]); setTariffa(imp.data.tariffa_oraria || 35);
  }, []);

  const loadPrev = useCallback(() => {
    if (!id) return;
    api.get(`/preventivi/${id}`).then(({ data }) => {
      setMeta({ numero: data.numero, scheda_id: data.scheda_id });
      setF(Object.fromEntries(Object.keys(EMPTY).map((k) => [k, data[k] ?? EMPTY[k]])));
    }).catch((e) => toast.error(formatError(e)));
  }, [id]);

  useEffect(() => {
    loadRefs().catch((e) => toast.error(formatError(e)));
    loadPrev();
  }, [loadRefs, loadPrev]);

  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));
  const t = totaliScheda(f);

  const save = async (silent = false) => {
    if (!f.cliente_id || !f.bici_id) { toast.error("Seleziona cliente e bici"); return false; }
    const payload = {
      ...f,
      validita_giorni: Number(f.validita_giorni) || 30,
      ricambi: f.ricambi.map((r) => ({ ...r, quantita: Number(r.quantita) || 1, prezzo: Number(r.prezzo) || 0, sconto: Number(r.sconto) || 0 })),
      manodopera: f.manodopera.filter((m) => m.descrizione.trim()).map((m) => ({ ...m, ore: Number(m.ore) || 0, tariffa: Number(m.tariffa) || 0, sconto: Number(m.sconto) || 0 })),
      sconto_totale: Number(f.sconto_totale) || 0,
    };
    setSaving(true);
    try {
      const { data } = id ? await api.put(`/preventivi/${id}`, payload) : await api.post("/preventivi", payload);
      if (!silent) toast.success(id ? "Preventivo aggiornato" : `Preventivo ${data.numero} creato`);
      if (!id) navigate(`/preventivi/${data.id}`, { replace: true });
      return true;
    } catch (e) {
      toast.error(formatError(e));
      return false;
    } finally {
      setSaving(false);
    }
  };

  const converti = async () => {
    if (!(await save(true))) return;
    setSaving(true);
    try {
      const { data } = await api.post(`/preventivi/${id}/converti`);
      toast.success(`Creata la scheda di lavoro #${data.numero}. I ricambi sono stati scalati dal magazzino.`);
      navigate(`/schede/${data.id}`);
    } catch (e) {
      toast.error(formatError(e));
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    try {
      await api.delete(`/preventivi/${id}`);
      toast.success("Preventivo eliminato");
      navigate("/preventivi");
    } catch (e) {
      toast.error(formatError(e));
    }
  };

  const clienteOpts = clienti.map((c) => ({ value: c.id, label: `${c.nome} ${c.cognome}`.trim(), hint: c.telefono }));
  const biciOpts = bici.filter((b) => b.cliente_id === f.cliente_id).map((b) => ({ value: b.id, label: `${b.marca} ${b.modello}`.trim(), hint: b.telaio }));

  return (
    <div>
      <Link to="/preventivi" className="mb-4 inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-900" data-testid="preventivo-back-link"><ArrowLeft className="h-4 w-4" /> Preventivi</Link>
      <PageHeader overline={id ? `Preventivo ${meta.numero || ""}` : "Nuovo preventivo"} title={id ? "Modifica preventivo di riparazione" : "Nuovo preventivo di riparazione"}>
        {id && <ConfirmDelete testId="preventivo-delete-button" onConfirm={remove} description="Il preventivo verrà eliminato definitivamente." />}
        {id && <Button variant="outline" onClick={() => navigate(`/preventivi/${id}/stampa`)} data-testid="preventivo-stampa-button"><Printer className="mr-1 h-4 w-4" /> Stampa / firma</Button>}
        {id && (meta.scheda_id
          ? <Button variant="outline" onClick={() => navigate(`/schede/${meta.scheda_id}`)} data-testid="preventivo-apri-scheda-button"><Wrench className="mr-1 h-4 w-4" /> Apri scheda di lavoro</Button>
          : <Button variant="outline" onClick={converti} disabled={saving} className="border-orange-300 text-orange-700" data-testid="preventivo-converti-button"><Wrench className="mr-1 h-4 w-4" /> Crea scheda di lavoro</Button>)}
        <Button onClick={() => save()} disabled={saving} className="bg-orange-600 hover:bg-orange-700" data-testid="preventivo-save-button"><Save className="mr-1 h-4 w-4" /> {saving ? "Salvataggio..." : "Salva preventivo"}</Button>
      </PageHeader>

      <div className="grid gap-6 xl:grid-cols-[1fr_340px]">
        <div className="space-y-6">
          <Section title="Cliente e bici" testId="preventivo-section-cliente">
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <div className="flex items-center justify-between"><L>Cliente *</L><button className="mb-1.5 text-xs font-semibold text-orange-600 hover:underline" onClick={() => setDlg("cliente")} data-testid="preventivo-quick-cliente-button"><Plus className="inline h-3 w-3" /> Nuovo</button></div>
                <SearchSelect options={clienteOpts} value={f.cliente_id} onChange={(v) => setF((s) => ({ ...s, cliente_id: v, bici_id: "" }))} placeholder="Seleziona cliente..." testId="preventivo-cliente-select" />
              </div>
              <div>
                <div className="flex items-center justify-between"><L>Bici *</L>{f.cliente_id && <button className="mb-1.5 text-xs font-semibold text-orange-600 hover:underline" onClick={() => setDlg("bici")} data-testid="preventivo-quick-bici-button"><Plus className="inline h-3 w-3" /> Nuova</button>}</div>
                <SearchSelect options={biciOpts} value={f.bici_id} onChange={(v) => set("bici_id", v)} placeholder={f.cliente_id ? "Seleziona bici..." : "Prima scegli il cliente"} testId="preventivo-bici-select" emptyText="Nessuna bici: aggiungine una" />
              </div>
              <div><L>Data preventivo</L><Input type="date" value={f.data} onChange={(e) => set("data", e.target.value)} data-testid="preventivo-data-input" /></div>
              <div><L>Validità (giorni)</L><Input type="number" min="1" max="365" value={f.validita_giorni} onChange={(e) => set("validita_giorni", e.target.value)} data-testid="preventivo-validita-input" /></div>
            </div>
          </Section>
          <Section title="Problema e lavori previsti" testId="preventivo-section-lavori">
            <div className="space-y-4">
              <div><L>Problema riscontrato / richiesta cliente</L><Textarea rows={3} value={f.problema} onChange={(e) => set("problema", e.target.value)} data-testid="preventivo-problema-input" /></div>
              <div><L>Lavori previsti</L><Textarea rows={3} value={f.lavori_previsti} onChange={(e) => set("lavori_previsti", e.target.value)} data-testid="preventivo-lavori-input" /></div>
            </div>
          </Section>
          <Section title="Ricambi e prodotti previsti" testId="preventivo-section-ricambi"><RicambiRows rows={f.ricambi} setRows={(v) => set("ricambi", v)} magazzino={magazzino} /></Section>
          <Section title="Manodopera prevista" testId="preventivo-section-manodopera"><ManodoperaRows rows={f.manodopera} setRows={(v) => set("manodopera", v)} tariffa={tariffa} /></Section>
          <Section title="Note"><Textarea rows={2} value={f.note} onChange={(e) => set("note", e.target.value)} data-testid="preventivo-note-input" /></Section>
        </div>

        <aside className="space-y-4 xl:sticky xl:top-24 xl:self-start">
          <div className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
            <L>Stato preventivo</L>
            <Select value={f.stato} onValueChange={(v) => set("stato", v)} disabled={!!meta.scheda_id}>
              <SelectTrigger data-testid="preventivo-status-select"><SelectValue /></SelectTrigger>
              <SelectContent>{STATI_PREVENTIVO.filter((s) => s.value !== "convertito" || meta.scheda_id).map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
            </Select>
            <p className="mt-3 text-xs text-slate-500" data-testid="preventivo-scadenza">Valido fino al <b>{dataIt(scadenza(f))}</b></p>
          </div>
          <div className="rounded-lg bg-slate-900 p-5 text-white shadow-sm">
            <TotaliConSconto f={f} setF={setF} t={t} etichetta="Totale preventivo" prefix="prev" />
            <p className="mt-2 text-[11px] text-slate-400">Prezzi IVA inclusa. Il magazzino non viene toccato finché il preventivo non diventa una scheda di lavoro.</p>
          </div>
        </aside>
      </div>

      <EntityFormDialog open={dlg === "cliente"} onOpenChange={(o) => !o && setDlg(null)} endpoint="clienti" fields={CLIENTE_FIELDS} title="Nuovo cliente" prefix="preventivo-cliente-form"
        onSaved={(c) => { loadRefs(); setF((s) => ({ ...s, cliente_id: c.id, bici_id: "" })); }} />
      <EntityFormDialog open={dlg === "bici"} onOpenChange={(o) => !o && setDlg(null)} endpoint="bici" fields={biciFields(clienti.filter((c) => c.id === f.cliente_id))} item={{ cliente_id: f.cliente_id }} title="Nuova bici" prefix="preventivo-bici-form"
        onSaved={(b) => { loadRefs(); set("bici_id", b.id); }} />
    </div>
  );
}
